import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './markerClusterStyles.css';
import api from '../api';

/**
 * SimpleClusteredMap - A component that renders a map with clustered markers
 * This version uses plain Leaflet JS directly rather than React-Leaflet
 * to avoid context errors and version conflicts
 */
const SimpleClusteredMap = () => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all reports
  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/reports/all');
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) {
      // Create map instance
      mapRef.current = L.map(mapContainerRef.current).setView([37.7749, -122.4194], 13);
      
      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapRef.current);

      // Create marker cluster group
      markerLayerRef.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        iconCreateFunction: (cluster) => {
          const markers = cluster.getAllChildMarkers();
          const count = markers.length;
          
          // Count markers by severity
          let level2Count = 0;
          let level3Count = 0;

          markers.forEach(marker => {
            const report = marker.report;
            if (report) {
              if (report.type === 'level_2') level2Count++;
              else if (report.type === 'level_3') level3Count++;
            }
          });
          
          // Determine color based on highest severity
          let clusterClass = 'marker-cluster-low';
          
          if (level3Count > 0) {
            clusterClass = 'marker-cluster-high';
          } else if (level2Count > 0) {
            clusterClass = 'marker-cluster-medium';
          }
          
          return L.divIcon({
            html: `<div><span>${count}</span></div>`,
            className: `marker-cluster ${clusterClass}`,
            iconSize: L.point(40, 40)
          });
        }
      });
      
      // Add marker layer to map
      mapRef.current.addLayer(markerLayerRef.current);

      // Fetch reports
      fetchReports();
    }
    
    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update markers when reports change
  useEffect(() => {
    if (mapRef.current && markerLayerRef.current && reports.length > 0) {
      // Clear existing markers
      markerLayerRef.current.clearLayers();
      
      // Add new markers
      reports.forEach(report => {
        if (report && report.position && typeof report.position.lat === 'number' && typeof report.position.lng === 'number') {
          const position = [report.position.lat, report.position.lng];
          
          // Set marker color based on report type
          const markerColor = 
            report.type === 'level_1' ? '#4caf50' : // green
            report.type === 'level_2' ? '#ff9800' : // orange
            report.type === 'level_3' ? '#f44336' : // red
            '#2196f3'; // blue (default)
          
          // Create custom marker icon
          const icon = L.divIcon({
            className: 'custom-marker-icon',
            html: `<div style="background-color: ${markerColor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>`,
            iconSize: [16, 16],
            iconAnchor: [8, 8]
          });
          
          // Create marker and add to layer
          const marker = L.marker(position, { icon });
          
          // Attach report data for cluster icon creation
          marker.report = report;
          
          // Add popup
          marker.bindPopup(`
            <div class="report-popup">
              <h3>${report.type === 'level_1' ? 'Low Severity' : 
                    report.type === 'level_2' ? 'Medium Severity' : 
                    report.type === 'level_3' ? 'High Severity' : 'Report'}</h3>
              <p>${report.description}</p>
              <p class="report-date">${new Date(report.timestamp).toLocaleString()}</p>
            </div>
          `);
          
          // Add to cluster group
          markerLayerRef.current.addLayer(marker);
        }
      });
    }
  }, [reports]);

  return (
    <div className="map-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>Loading reports...</span>
        </div>
      )}
      <div 
        ref={mapContainerRef} 
        style={{ height: '100%', width: '100%', minHeight: '600px' }}
      />
    </div>
  );
};

export default SimpleClusteredMap;
